/**
 * NARA Design Studio — Visual design tool for non-programmers.
 * 3-panel layout: Screen Navigator | Visual Canvas | Live Pseudocode
 * Bottom: AI Assistant chat
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Chip, IconButton, Tooltip,
  CircularProgress, Select, MenuItem, FormControl, InputLabel,
  Badge, Avatar, Divider, InputAdornment, Alert,
  Switch, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Dashboard as DashIcon, Person as PersonIcon, MedicalServices as MedicalIcon,
  Assignment as AssignIcon, Biotech as BiotechIcon, MonitorHeart as VitalsIcon,
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon,
  Send as SendIcon, Check as CheckIcon, Close as CloseIcon, Undo as UndoIcon,
  Psychology as AIIcon, Code as CodeIcon, Visibility as PreviewIcon,
  ArrowBack as BackIcon, Refresh as RefreshIcon, Search as SearchIcon,
  TextFields as TextIcon, Numbers as NumberIcon, CalendarMonth as DateIcon,
  ArrowDropDown as SelectIcon, CheckBox as CheckboxIcon, ToggleOn as ToggleIcon,
  Description as TextAreaIcon, LocalPhone as PhoneIcon, Email as EmailIcon,
  DragIndicator as DragIcon, Star as StarIcon,
} from '@mui/icons-material';

import { auth } from '../firebase';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MODULO_ICONOS = {
  emr: <PersonIcon />,
  consulta: <MedicalIcon />,
  expediente: <AssignIcon />,
};

const TIPO_CAMPO_ICONOS = {
  texto: <TextIcon sx={{ fontSize: 14 }} />,
  texto_largo: <TextAreaIcon sx={{ fontSize: 14 }} />,
  numero: <NumberIcon sx={{ fontSize: 14 }} />,
  decimal: <NumberIcon sx={{ fontSize: 14 }} />,
  fecha: <DateIcon sx={{ fontSize: 14 }} />,
  selector: <SelectIcon sx={{ fontSize: 14 }} />,
  checkbox: <CheckboxIcon sx={{ fontSize: 14 }} />,
  toggle: <ToggleIcon sx={{ fontSize: 14 }} />,
  telefono: <PhoneIcon sx={{ fontSize: 14 }} />,
  email: <EmailIcon sx={{ fontSize: 14 }} />,
  presion: <VitalsIcon sx={{ fontSize: 14 }} />,
};

const TIPOS_CAMPO = [
  { value: 'texto', label: 'Texto' },
  { value: 'texto_largo', label: 'Texto largo' },
  { value: 'numero', label: 'Número' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'fecha', label: 'Fecha' },
  { value: 'selector', label: 'Selector' },
  { value: 'checkbox', label: 'Casilla' },
  { value: 'toggle', label: 'Interruptor' },
  { value: 'telefono', label: 'Teléfono' },
  { value: 'email', label: 'Email' },
];

function SortableField({ campo, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: campo.clave });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 'auto' };
  return (
    <Box ref={setNodeRef} style={style} sx={{
      display: 'flex', alignItems: 'center', gap: 1, p: 1.2,
      borderRadius: 2, background: isDragging ? '#1e293b' : '#0f172a', border: `1px solid ${isDragging ? '#6366f1' : '#334155'}`,
      transition: 'all 0.2s',
      '&:hover': { borderColor: '#6366f1', '& .actions': { opacity: 1 } },
    }}>
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', '&:active': { cursor: 'grabbing' } }}>
        <DragIcon sx={{ fontSize: 16, color: '#475569' }} />
      </Box>
      <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {TIPO_CAMPO_ICONOS[campo.tipo] || <TextIcon sx={{ fontSize: 14, color: '#94a3b8' }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{campo.nombre}</Typography>
        <Typography sx={{ fontSize: 9, color: '#64748b' }}>
          {campo.tipo}{campo.obligatorio ? ' · obligatorio' : ''}{campo.opciones ? ` · ${campo.opciones.length} opciones` : ''}{campo.unidad ? ` · ${campo.unidad}` : ''}
        </Typography>
      </Box>
      {campo.obligatorio && <Chip label="REQ" size="small" sx={{ height: 18, fontSize: 8, fontWeight: 700, bgcolor: '#7c3aed', color: '#fff' }} />}
      <Box className="actions" sx={{ opacity: 0, transition: 'opacity 0.2s', display: 'flex', gap: 0.3 }}>
        <Tooltip title="Editar campo"><IconButton size="small" onClick={onEdit} sx={{ color: '#64748b', '&:hover': { color: '#f59e0b' } }}><EditIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
        <Tooltip title="Eliminar campo"><IconButton size="small" onClick={onDelete} sx={{ color: '#64748b', '&:hover': { color: '#ef4444' } }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
      </Box>
    </Box>
  );
}

export default function DesignStudio() {
  const [userId, setUserId] = useState(null);
  const [pantallas, setPantallas] = useState([]);
  const [pantallaActiva, setPantallaActiva] = useState(null);
  const [seccionActiva, setSeccionActiva] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [propuestas, setPropuestas] = useState([]);
  const [propuestaActual, setPropuestaActual] = useState(null);
  const [cambios, setCambios] = useState([]);
  const [vistaActiva, setVistaActiva] = useState('diseno'); // diseno | propuestas
  const [mensajeIA, setMensajeIA] = useState('');
  const [chatIA, setChatIA] = useState([
    { role: 'ia', text: 'Bienvenido al Design Studio. Selecciona una pantalla del menú izquierdo para empezar a diseñar. Puedo sugerirte mejoras o ayudarte a crear propuestas.' }
  ]);
  const [inputIA, setInputIA] = useState('');
  const chatRef = useRef(null);

  // Nuevo campo form
  const [nuevoCampo, setNuevoCampo] = useState({ nombre: '', clave: '', tipo: 'texto', obligatorio: false, opciones: '' });
  const [mostrarFormCampo, setMostrarFormCampo] = useState(false);

  // Edición de campo existente
  const [campoEditando, setCampoEditando] = useState(null);
  const [campoEditandoClave, setCampoEditandoClave] = useState(null);
  const [iaLoading, setIaLoading] = useState(false);
  const [vistaCanvas, setVistaCanvas] = useState('estructura'); // estructura | preview
  const [pantallaOriginal, setPantallaOriginal] = useState(null); // snapshot para "antes"
  const [nuevaFuncionalidad, setNuevaFuncionalidad] = useState({ titulo: '', descripcion: '', prioridad: 'normal', referencia: '' });

  // Auth
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (u) {
        const storedId = localStorage.getItem('user_id');
        if (storedId) { setUserId(storedId); } else {
          setUserId(u.uid);
          try {
            const res = await fetch(`${API_URL}/users/by_uid/${u.uid}`);
            const data = await res.json();
            if (data?.id) { localStorage.setItem('user_id', String(data.id)); setUserId(String(data.id)); }
          } catch (e) { /* keep uid */ }
        }
      }
    });
    return () => unsub();
  }, []);

  // Cargar pantallas
  const cargarPantallas = useCallback(async () => {
    setCargando(true);
    try {
      const res = await fetch(`${API_URL}/design-studio/pantallas`, { headers: { 'X-User-Id': userId } });
      if (res.ok) {
        const data = await res.json();
        setPantallas(Array.isArray(data) ? data : (data.pantallas || []));
      }
    } catch (err) { console.error(err); }
    setCargando(false);
  }, [userId]);

  const cargarPropuestas = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/design-studio/propuestas`, { headers: { 'X-User-Id': userId } });
      if (res.ok) {
        const data = await res.json();
        setPropuestas(Array.isArray(data) ? data : (data.propuestas || []));
      }
    } catch (err) { console.error(err); }
  }, [userId]);

  useEffect(() => { if (userId) { cargarPantallas(); cargarPropuestas(); } }, [userId, cargarPantallas, cargarPropuestas]);

  const seleccionarPantalla = (p) => {
    setPantallaActiva(JSON.parse(JSON.stringify(p)));
    setPantallaOriginal(JSON.parse(JSON.stringify(p)));
    setSeccionActiva(p.secciones?.[0] || null);
    setCambios([]);
    setPropuestaActual(null);
    setMostrarFormCampo(false);
    setVistaCanvas('estructura');
    agregarMensajeIA(`Pantalla seleccionada: "${p.nombre}". Tiene ${p.secciones?.length || 0} secciones. ¿Qué te gustaría modificar?`);
  };

  const agregarMensajeIA = (text) => {
    setChatIA(prev => [...prev, { role: 'ia', text }]);
    setTimeout(() => chatRef.current?.scrollTo(0, chatRef.current.scrollHeight), 100);
  };

  const enviarMensajeIA = async () => {
    if (!inputIA.trim()) return;
    const msg = inputIA;
    setChatIA(prev => [...prev, { role: 'user', text: msg }]);
    setInputIA('');
    setIaLoading(true);
    try {
      const res = await fetch(`${API_URL}/design-studio/asistente-ia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({
          mensaje: msg,
          pantalla: pantallaActiva?.clave || '',
          seccion: seccionActiva?.clave || '',
          campos_actuales: seccionActiva?.campos || [],
          cambios_pendientes: cambios,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        agregarMensajeIA(data.respuesta || 'No tengo una respuesta para eso.');
        if (data.sugerencias?.length > 0) {
          data.sugerencias.forEach(s => {
            agregarMensajeIA(`💡 Sugerencia: ${s.razon || ''} → ${s.tipo === 'agregar_campo' ? `Agregar "${s.campo?.nombre}" (${s.campo?.tipo})` : s.tipo}`);
          });
        }
      } else {
        agregarMensajeIA('Puedo ayudarte con: agregar campos, modificar campos existentes, reordenar secciones. ¿Qué quieres hacer?');
      }
    } catch (err) {
      agregarMensajeIA('Error conectando con el asistente. Intenta de nuevo.');
    }
    setIaLoading(false);
  };

  // Iniciar edición de campo
  const iniciarEdicion = (campo) => {
    setCampoEditandoClave(campo.clave);
    setCampoEditando({
      nombre: campo.nombre,
      tipo: campo.tipo,
      obligatorio: campo.obligatorio || false,
      opciones: campo.opciones ? campo.opciones.join(', ') : '',
      unidad: campo.unidad || '',
    });
    setMostrarFormCampo(false);
  };

  // Guardar edición
  const guardarEdicion = () => {
    if (!campoEditando || !campoEditandoClave || !seccionActiva) return;
    const campoAnterior = (seccionActiva.campos || []).find(c => c.clave === campoEditandoClave);
    const campoNuevo = {
      clave: campoEditandoClave,
      nombre: campoEditando.nombre,
      tipo: campoEditando.tipo,
      obligatorio: campoEditando.obligatorio,
      ...(campoEditando.opciones && { opciones: campoEditando.opciones.split(',').map(o => o.trim()).filter(Boolean) }),
      ...(campoEditando.unidad && { unidad: campoEditando.unidad }),
    };

    const cambio = {
      accion: 'modificar_campo',
      seccion: seccionActiva.clave,
      campo_clave: campoEditandoClave,
      campo_nombre: campoAnterior?.nombre || campoEditandoClave,
      cambios_campo: {},
      timestamp: new Date().toISOString(),
    };
    if (campoAnterior?.nombre !== campoNuevo.nombre) cambio.cambios_campo.nombre = `"${campoAnterior?.nombre}" → "${campoNuevo.nombre}"`;
    if (campoAnterior?.tipo !== campoNuevo.tipo) cambio.cambios_campo.tipo = `${campoAnterior?.tipo} → ${campoNuevo.tipo}`;
    if (campoAnterior?.obligatorio !== campoNuevo.obligatorio) cambio.cambios_campo.obligatorio = campoNuevo.obligatorio ? 'sí' : 'no';
    if (JSON.stringify(campoAnterior?.opciones) !== JSON.stringify(campoNuevo.opciones)) cambio.cambios_campo.opciones = campoNuevo.opciones?.join(', ') || '—';

    if (Object.keys(cambio.cambios_campo).length > 0) {
      setCambios(prev => [...prev, cambio]);
    }

    setPantallaActiva(prev => ({
      ...prev,
      secciones: prev.secciones.map(s =>
        s.clave === seccionActiva.clave
          ? { ...s, campos: (s.campos || []).map(c => c.clave === campoEditandoClave ? campoNuevo : c) }
          : s
      ),
    }));
    setSeccionActiva(prev => ({
      ...prev,
      campos: (prev.campos || []).map(c => c.clave === campoEditandoClave ? campoNuevo : c),
    }));

    agregarMensajeIA(`Campo "${campoNuevo.nombre}" modificado.`);
    setCampoEditando(null);
    setCampoEditandoClave(null);
  };

  const cancelarEdicion = () => { setCampoEditando(null); setCampoEditandoClave(null); };

  // Drag & drop reorder
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id || !seccionActiva) return;
    const campos = seccionActiva.campos || [];
    const oldIdx = campos.findIndex(c => c.clave === active.id);
    const newIdx = campos.findIndex(c => c.clave === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordenados = arrayMove(campos, oldIdx, newIdx);

    setCambios(prev => [...prev, {
      accion: 'reordenar_campo',
      seccion: seccionActiva.clave,
      campo_nombre: campos[oldIdx].nombre,
      de_posicion: oldIdx + 1,
      a_posicion: newIdx + 1,
      timestamp: new Date().toISOString(),
    }]);

    setSeccionActiva(prev => ({ ...prev, campos: reordenados }));
    setPantallaActiva(prev => ({
      ...prev,
      secciones: prev.secciones.map(s => s.clave === seccionActiva.clave ? { ...s, campos: reordenados } : s),
    }));
    agregarMensajeIA(`Campo "${campos[oldIdx].nombre}" movido de posición ${oldIdx + 1} a ${newIdx + 1}.`);
  };

  // Preview: render con componentes MUI reales (mismos estilos que el EMR)
  const previewFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13 }, '& .MuiInputLabel-root': { color: '#64748b' } };
  const renderPreviewCampo = (campo) => {
    const label = `${campo.nombre}${campo.obligatorio ? ' *' : ''}`;
    switch (campo.tipo) {
      case 'selector':
        return (
          <FormControl fullWidth size="small">
            <InputLabel shrink>{label}</InputLabel>
            <Select notched label={label} displayEmpty value="" sx={{ ...previewFieldSx['& .MuiOutlinedInput-root'] }}>
              <MenuItem value=""><em>Seleccionar...</em></MenuItem>
              {(campo.opciones || []).map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        );
      case 'fecha':
        return <TextField fullWidth size="small" label={label} type="date" InputLabelProps={{ shrink: true }} sx={previewFieldSx} />;
      case 'checkbox':
        return <FormControlLabel control={<Checkbox size="small" />} label={<Typography sx={{ fontSize: 13 }}>{campo.nombre}</Typography>} />;
      case 'toggle':
        return <FormControlLabel control={<Switch size="small" />} label={<Typography sx={{ fontSize: 13 }}>{campo.nombre}</Typography>} />;
      case 'texto_largo':
        return <TextField fullWidth size="small" label={label} multiline minRows={3} placeholder={`Escribir ${campo.nombre.toLowerCase()}...`} InputLabelProps={{ shrink: true }} sx={previewFieldSx} />;
      case 'numero': case 'decimal':
        return <TextField fullWidth size="small" label={label} type="number" placeholder="0" InputLabelProps={{ shrink: true }}
          InputProps={campo.unidad ? { endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 10, color: '#94a3b8' }}>{campo.unidad}</Typography></InputAdornment> } : undefined}
          sx={previewFieldSx} />;
      case 'presion':
        return (
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ef4444', mb: 0.3 }}>{campo.nombre}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <TextField size="small" placeholder="120" inputProps={{ style: { textAlign: 'center', fontWeight: 600, fontSize: 14, padding: '6px 2px' } }}
                sx={{ width: 52, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff' } }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#94a3b8' }}>/</Typography>
              <TextField size="small" placeholder="80" inputProps={{ style: { textAlign: 'center', fontWeight: 600, fontSize: 14, padding: '6px 2px' } }}
                sx={{ width: 52, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff' } }} />
              <Typography sx={{ fontSize: 9, color: '#94a3b8', ml: 0.3 }}>mmHg</Typography>
            </Box>
          </Box>
        );
      case 'telefono':
        return <TextField fullWidth size="small" label={label} placeholder="+52 55 1234 5678" InputLabelProps={{ shrink: true }}
          InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} sx={previewFieldSx} />;
      case 'email':
        return <TextField fullWidth size="small" label={label} placeholder="correo@ejemplo.com" InputLabelProps={{ shrink: true }}
          InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} sx={previewFieldSx} />;
      default:
        return <TextField fullWidth size="small" label={label} placeholder={`Escribir ${campo.nombre.toLowerCase()}...`} InputLabelProps={{ shrink: true }} sx={previewFieldSx} />;
    }
  };

  // Agregar campo
  const agregarCampoAPantalla = () => {
    if (!nuevoCampo.nombre || !seccionActiva) return;
    const clave = nuevoCampo.clave || nuevoCampo.nombre.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const campo = {
      clave,
      nombre: nuevoCampo.nombre,
      tipo: nuevoCampo.tipo,
      obligatorio: nuevoCampo.obligatorio,
      ...(nuevoCampo.opciones && { opciones: nuevoCampo.opciones.split(',').map(o => o.trim()) }),
    };

    const cambio = {
      accion: 'agregar_campo',
      seccion: seccionActiva.clave,
      campo,
      timestamp: new Date().toISOString(),
    };
    setCambios(prev => [...prev, cambio]);

    // Update local preview
    setSeccionActiva(prev => ({
      ...prev,
      campos: [...(prev.campos || []), campo],
    }));

    // Update in pantalla
    setPantallaActiva(prev => ({
      ...prev,
      secciones: prev.secciones.map(s =>
        s.clave === seccionActiva.clave
          ? { ...s, campos: [...(s.campos || []), campo] }
          : s
      ),
    }));

    setNuevoCampo({ nombre: '', clave: '', tipo: 'texto', obligatorio: false, opciones: '' });
    setMostrarFormCampo(false);
    agregarMensajeIA(`Campo "${campo.nombre}" (${campo.tipo}) agregado a la sección "${seccionActiva.nombre}".`);
  };

  // Eliminar campo
  const eliminarCampo = (seccionClave, campoClave, campoNombre) => {
    const cambio = {
      accion: 'eliminar_campo',
      seccion: seccionClave,
      campo_clave: campoClave,
      campo_nombre: campoNombre,
      timestamp: new Date().toISOString(),
    };
    setCambios(prev => [...prev, cambio]);

    setPantallaActiva(prev => ({
      ...prev,
      secciones: prev.secciones.map(s =>
        s.clave === seccionClave
          ? { ...s, campos: (s.campos || []).filter(c => c.clave !== campoClave) }
          : s
      ),
    }));
    if (seccionActiva?.clave === seccionClave) {
      setSeccionActiva(prev => ({
        ...prev,
        campos: (prev.campos || []).filter(c => c.clave !== campoClave),
      }));
    }
    agregarMensajeIA(`Campo "${campoNombre}" eliminado de la propuesta.`);
  };

  // Generar pseudocódigo
  const generarPseudocodigo = () => {
    if (cambios.length === 0) return '// Sin cambios aún\n// Selecciona una pantalla y empieza a diseñar';
    let code = `PANTALLA: ${pantallaActiva?.nombre || '—'}\n\n`;
    cambios.forEach((c, i) => {
      if (c.accion === 'agregar_campo') {
        code += `+ AGREGAR campo "${c.campo.nombre}"\n`;
        code += `    tipo: ${c.campo.tipo}\n`;
        if (c.campo.obligatorio) code += `    obligatorio: sí\n`;
        if (c.campo.opciones) code += `    opciones: [${c.campo.opciones.join(', ')}]\n`;
        code += `    sección: ${c.seccion}\n`;
      } else if (c.accion === 'eliminar_campo') {
        code += `- ELIMINAR campo "${c.campo_nombre}"\n`;
        code += `    sección: ${c.seccion}\n`;
      } else if (c.accion === 'modificar_campo') {
        code += `~ MODIFICAR campo "${c.campo_nombre}"\n`;
        code += `    sección: ${c.seccion}\n`;
        Object.entries(c.cambios_campo || {}).forEach(([k, v]) => {
          code += `    ${k}: ${v}\n`;
        });
      } else if (c.accion === 'reordenar_campo') {
        code += `↕ MOVER campo "${c.campo_nombre}"\n`;
        code += `    posición: ${c.de_posicion} → ${c.a_posicion}\n`;
        code += `    sección: ${c.seccion}\n`;
      }
      if (i < cambios.length - 1) code += '\n';
    });
    return code;
  };

  // Guardar propuesta
  const guardarPropuesta = async (enviar = false) => {
    if (cambios.length === 0 || !pantallaActiva) return;
    try {
      const body = {
        titulo: `Modificación de ${pantallaActiva.nombre}`,
        descripcion: `${cambios.length} cambio(s) propuesto(s)`,
        pantalla: pantallaActiva.clave,
        tipo: 'modificacion',
        cambios,
        pseudocodigo: generarPseudocodigo(),
      };
      const res = await fetch(`${API_URL}/design-studio/propuestas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        if (enviar) {
          await fetch(`${API_URL}/design-studio/propuestas/${data.id}/enviar-revision`, {
            method: 'POST', headers: { 'X-User-Id': userId },
          });
          agregarMensajeIA('Propuesta enviada para revisión. Raul será notificado.');
        } else {
          agregarMensajeIA('Propuesta guardada como borrador.');
        }
        setCambios([]);
        cargarPropuestas();
      }
    } catch (err) { console.error(err); }
  };

  if (cargando) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', background: '#0f172a' }}>
      {/* Header */}
      <Box sx={{
        px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderBottom: '1px solid #334155',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CodeIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: 0.5 }}>NARA Design Studio</Typography>
            <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Diseña · Propón · Transforma</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label="Diseñar" size="small"
            onClick={() => setVistaActiva('diseno')}
            sx={{ fontWeight: 600, fontSize: 11, cursor: 'pointer', bgcolor: vistaActiva === 'diseno' ? '#6366f1' : '#1e293b', color: '#fff', border: '1px solid #475569' }}
          />
          <Chip label="Nueva idea" size="small"
            onClick={() => setVistaActiva('nueva_func')}
            icon={<AddIcon sx={{ fontSize: 14, color: vistaActiva === 'nueva_func' ? '#fff !important' : '#10b981 !important' }} />}
            sx={{ fontWeight: 600, fontSize: 11, cursor: 'pointer', bgcolor: vistaActiva === 'nueva_func' ? '#10b981' : '#1e293b', color: '#fff', border: '1px solid #475569' }}
          />
          <Chip label={`Propuestas (${propuestas.length})`} size="small"
            onClick={() => setVistaActiva('propuestas')}
            sx={{ fontWeight: 600, fontSize: 11, cursor: 'pointer', bgcolor: vistaActiva === 'propuestas' ? '#6366f1' : '#1e293b', color: '#fff', border: '1px solid #475569' }}
          />
        </Box>
      </Box>

      {vistaActiva === 'diseno' ? (
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Panel 1: Navegador de pantallas */}
          <Box sx={{ width: 200, flexShrink: 0, background: '#1e293b', borderRight: '1px solid #334155', overflowY: 'auto' }}>
            <Typography sx={{ px: 2, py: 1.5, fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 1, textTransform: 'uppercase' }}>
              Pantallas
            </Typography>
            {pantallas.map(p => {
              const activa = pantallaActiva?.clave === p.clave;
              return (
                <Box key={p.clave} onClick={() => seleccionarPantalla(p)} sx={{
                  px: 2, py: 1.2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                  background: activa ? '#334155' : 'transparent',
                  borderLeft: activa ? '3px solid #8b5cf6' : '3px solid transparent',
                  '&:hover': { background: '#334155' },
                }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: 1.5, background: activa ? '#8b5cf6' : '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {React.cloneElement(MODULO_ICONOS[p.modulo] || <DashIcon />, { sx: { fontSize: 14, color: '#fff' } })}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: activa ? 700 : 500, color: activa ? '#fff' : '#94a3b8' }} noWrap>{p.nombre}</Typography>
                    <Typography sx={{ fontSize: 9, color: '#64748b' }}>{p.secciones?.length || 0} secciones</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Panel 2: Canvas visual */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0f172a' }}>
            {!pantallaActiva ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <CodeIcon sx={{ fontSize: 64, color: '#334155', mb: 2 }} />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#64748b' }}>Selecciona una pantalla</Typography>
                <Typography sx={{ fontSize: 12, color: '#475569' }}>del menú izquierdo para empezar a diseñar</Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {/* Título de pantalla */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{pantallaActiva.nombre}</Typography>
                    <Chip label={pantallaActiva.modulo} size="small" sx={{ fontSize: 9, height: 20, bgcolor: '#334155', color: '#94a3b8' }} />
                  </Box>
                  {cambios.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" startIcon={<SaveIcon sx={{ fontSize: 14 }} />} onClick={() => guardarPropuesta(false)}
                        sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, color: '#94a3b8', border: '1px solid #475569', borderRadius: 2, '&:hover': { background: '#1e293b' } }}>
                        Guardar borrador
                      </Button>
                      <Button size="small" startIcon={<SendIcon sx={{ fontSize: 14 }} />} onClick={() => guardarPropuesta(true)}
                        sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, color: '#fff', background: '#6366f1', borderRadius: 2, '&:hover': { background: '#4f46e5' } }}>
                        Enviar propuesta ({cambios.length})
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Secciones como tabs */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                  {pantallaActiva.secciones?.map(s => (
                    <Chip key={s.clave} label={s.nombre} size="small"
                      onClick={() => setSeccionActiva(s)}
                      sx={{
                        fontSize: 11, fontWeight: seccionActiva?.clave === s.clave ? 700 : 500, cursor: 'pointer',
                        bgcolor: seccionActiva?.clave === s.clave ? '#6366f1' : '#1e293b',
                        color: '#fff', border: '1px solid #475569',
                        '&:hover': { bgcolor: seccionActiva?.clave === s.clave ? '#6366f1' : '#334155' },
                      }}
                    />
                  ))}
                </Box>

                {/* Canvas: campos de la sección */}
                {seccionActiva && (
                  <Box sx={{ p: 2, borderRadius: 3, background: '#1e293b', border: '1px solid #334155' }}>
                    {/* Header con toggle vista */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                        {seccionActiva.nombre}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.3 }}>
                        {[
                          { key: 'estructura', label: 'Estructura', icon: <DragIcon sx={{ fontSize: 12 }} />, color: '#6366f1' },
                          { key: 'preview', label: 'Preview', icon: <PreviewIcon sx={{ fontSize: 12 }} />, color: '#10b981' },
                          { key: 'comparar', label: 'Antes / Después', icon: <CodeIcon sx={{ fontSize: 12 }} />, color: '#f59e0b' },
                        ].map(v => (
                          <Chip key={v.key} label={v.label} size="small" onClick={() => setVistaCanvas(v.key)}
                            icon={React.cloneElement(v.icon, { sx: { fontSize: 12, color: vistaCanvas === v.key ? '#fff !important' : '#64748b !important' } })}
                            sx={{ fontSize: 9, fontWeight: 600, cursor: 'pointer', height: 24, bgcolor: vistaCanvas === v.key ? v.color : '#0f172a', color: vistaCanvas === v.key ? '#fff' : '#64748b', border: '1px solid #475569' }} />
                        ))}
                      </Box>
                    </Box>

                    {(seccionActiva.campos || []).length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: '#64748b', py: 2, textAlign: 'center' }}>
                        Esta sección no tiene campos configurables
                      </Typography>
                    ) : vistaCanvas === 'preview' ? (
                      /* Vista preview visual — replica el estilo real del EMR */
                      <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        <Box sx={{ px: 2, py: 1, background: 'linear-gradient(135deg, #f8fafc, #eef2ff)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e3a5f' }}>{seccionActiva.nombre}</Typography>
                          <Chip label="Con cambios" size="small" sx={{ height: 18, fontSize: 8, fontWeight: 600, bgcolor: '#10b981', color: '#fff', ml: 'auto' }} />
                        </Box>
                        <Box sx={{ p: 2.5, background: '#fff' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            {(seccionActiva.campos || []).map(campo => (
                              <Box key={campo.clave} sx={{ ...(campo.tipo === 'texto_largo' ? { gridColumn: '1 / -1' } : {}) }}>{renderPreviewCampo(campo)}</Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ) : vistaCanvas === 'comparar' ? (
                      /* Vista Antes / Después */
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        {/* ANTES */}
                        <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #475569' }}>
                          <Box sx={{ px: 2, py: 1, background: '#1e293b', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#64748b' }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>Antes (actual)</Typography>
                          </Box>
                          <Box sx={{ p: 2, background: '#fff' }}>
                            {(() => {
                              const secOriginal = pantallaOriginal?.secciones?.find(s => s.clave === seccionActiva?.clave);
                              const camposOrig = secOriginal?.campos || [];
                              return camposOrig.length === 0 ? (
                                <Typography sx={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', py: 3 }}>Sin campos</Typography>
                              ) : (
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
                                  {camposOrig.map(c => <Box key={c.clave}>{renderPreviewCampo(c)}</Box>)}
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                        {/* DESPUÉS */}
                        <Box sx={{ borderRadius: 3, overflow: 'hidden', border: cambios.length > 0 ? '2px solid #10b981' : '1px solid #475569' }}>
                          <Box sx={{ px: 2, py: 1, background: cambios.length > 0 ? '#064e3b' : '#1e293b', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: cambios.length > 0 ? '#10b981' : '#64748b' }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: cambios.length > 0 ? '#6ee7b7' : '#94a3b8' }}>Después (propuesto)</Typography>
                            {cambios.length > 0 && <Chip label={`${cambios.length} cambios`} size="small" sx={{ height: 16, fontSize: 8, bgcolor: '#10b981', color: '#fff', ml: 'auto' }} />}
                          </Box>
                          <Box sx={{ p: 2, background: '#fff' }}>
                            {(seccionActiva?.campos || []).length === 0 ? (
                              <Typography sx={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', py: 3 }}>Sin campos</Typography>
                            ) : (
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
                                {(seccionActiva?.campos || []).map(c => <Box key={c.clave}>{renderPreviewCampo(c)}</Box>)}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      /* Vista estructura con drag & drop */
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={(seccionActiva.campos || []).map(c => c.clave)} strategy={verticalListSortingStrategy}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                            {(seccionActiva.campos || []).map((campo) => (
                              campoEditandoClave === campo.clave ? (
                                <Box key={campo.clave} sx={{ p: 1.5, borderRadius: 2, background: '#0f172a', border: '2px solid #f59e0b' }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>Editando campo</Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                    <TextField size="small" label="Nombre" value={campoEditando?.nombre || ''} onChange={(e) => setCampoEditando(prev => ({ ...prev, nombre: e.target.value }))}
                                      InputLabelProps={{ shrink: true }}
                                      sx={{ flex: 1, minWidth: 150, '& .MuiOutlinedInput-root': { background: '#1e293b', color: '#fff', fontSize: 12 }, '& .MuiInputLabel-root': { color: '#64748b' } }} />
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <InputLabel shrink sx={{ color: '#64748b' }}>Tipo</InputLabel>
                                      <Select notched label="Tipo" value={campoEditando?.tipo || 'texto'} onChange={(e) => setCampoEditando(prev => ({ ...prev, tipo: e.target.value }))}
                                        sx={{ background: '#1e293b', color: '#fff', fontSize: 12 }}>
                                        {TIPOS_CAMPO.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                  {campoEditando?.tipo === 'selector' && (
                                    <TextField size="small" label="Opciones (separadas por coma)" value={campoEditando?.opciones || ''} onChange={(e) => setCampoEditando(prev => ({ ...prev, opciones: e.target.value }))} fullWidth
                                      InputLabelProps={{ shrink: true }}
                                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { background: '#1e293b', color: '#fff', fontSize: 12 }, '& .MuiInputLabel-root': { color: '#64748b' } }} />
                                  )}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Chip label={campoEditando?.obligatorio ? 'Obligatorio' : 'Opcional'} size="small"
                                      onClick={() => setCampoEditando(prev => ({ ...prev, obligatorio: !prev.obligatorio }))}
                                      sx={{ cursor: 'pointer', fontSize: 10, fontWeight: 600, bgcolor: campoEditando?.obligatorio ? '#7c3aed' : '#334155', color: '#fff' }} />
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <Button size="small" onClick={cancelarEdicion} sx={{ textTransform: 'none', fontSize: 11, color: '#64748b' }}>Cancelar</Button>
                                      <Button size="small" variant="contained" onClick={guardarEdicion}
                                        sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, background: '#f59e0b', '&:hover': { background: '#d97706' } }}>Guardar</Button>
                                    </Box>
                                  </Box>
                                </Box>
                              ) : (
                                <SortableField key={campo.clave} campo={campo} onEdit={() => iniciarEdicion(campo)}
                                  onDelete={() => eliminarCampo(seccionActiva.clave, campo.clave, campo.nombre)} />
                              )
                            ))}
                          </Box>
                        </SortableContext>
                      </DndContext>
                    )}

                    {/* Agregar campo */}
                    {!mostrarFormCampo ? (
                      <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setMostrarFormCampo(true)}
                        sx={{ mt: 1.5, textTransform: 'none', fontSize: 11, fontWeight: 600, color: '#8b5cf6', border: '1px dashed #6366f1', borderRadius: 2, width: '100%', '&:hover': { background: '#6366f120' } }}>
                        Agregar campo
                      </Button>
                    ) : (
                      <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, background: '#0f172a', border: '2px solid #6366f1' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Nuevo campo</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <TextField size="small" label="Nombre del campo" placeholder="Ej: Grupo sanguíneo" value={nuevoCampo.nombre}
                            onChange={(e) => setNuevoCampo({ ...nuevoCampo, nombre: e.target.value, clave: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { background: '#1e293b', color: '#fff', fontSize: 12 }, '& .MuiInputLabel-root': { color: '#64748b' } }}
                          />
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel shrink sx={{ color: '#64748b' }}>Tipo</InputLabel>
                            <Select notched label="Tipo" value={nuevoCampo.tipo} onChange={(e) => setNuevoCampo({ ...nuevoCampo, tipo: e.target.value })}
                              sx={{ background: '#1e293b', color: '#fff', fontSize: 12 }}>
                              {TIPOS_CAMPO.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Box>
                        {nuevoCampo.tipo === 'selector' && (
                          <TextField size="small" label="Opciones (separadas por coma)" placeholder="Ej: A+, A-, B+, B-, O+, O-" value={nuevoCampo.opciones}
                            onChange={(e) => setNuevoCampo({ ...nuevoCampo, opciones: e.target.value })} fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 1, '& .MuiOutlinedInput-root': { background: '#1e293b', color: '#fff', fontSize: 12 }, '& .MuiInputLabel-root': { color: '#64748b' } }}
                          />
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip label={nuevoCampo.obligatorio ? 'Obligatorio' : 'Opcional'} size="small"
                            onClick={() => setNuevoCampo({ ...nuevoCampo, obligatorio: !nuevoCampo.obligatorio })}
                            sx={{ cursor: 'pointer', fontSize: 10, fontWeight: 600, bgcolor: nuevoCampo.obligatorio ? '#7c3aed' : '#334155', color: '#fff' }}
                          />
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button size="small" onClick={() => setMostrarFormCampo(false)}
                              sx={{ textTransform: 'none', fontSize: 11, color: '#64748b' }}>Cancelar</Button>
                            <Button size="small" variant="contained" disabled={!nuevoCampo.nombre} onClick={agregarCampoAPantalla}
                              sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, background: '#6366f1', '&:hover': { background: '#4f46e5' } }}>
                              Agregar
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Chat IA */}
            <Box sx={{ borderTop: '1px solid #334155', background: '#1e293b' }}>
              <Box ref={chatRef} sx={{ maxHeight: 120, overflowY: 'auto', px: 2, py: 1 }}>
                {chatIA.map((msg, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 0.8, mb: 0.8, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    {msg.role === 'ia' && <AIIcon sx={{ fontSize: 16, color: '#8b5cf6', mt: 0.3, flexShrink: 0 }} />}
                    <Typography sx={{
                      fontSize: 11, lineHeight: 1.5, maxWidth: '80%',
                      color: msg.role === 'ia' ? '#94a3b8' : '#fff',
                      background: msg.role === 'user' ? '#334155' : 'transparent',
                      px: msg.role === 'user' ? 1.5 : 0, py: msg.role === 'user' ? 0.5 : 0,
                      borderRadius: 2,
                    }}>{msg.text}</Typography>
                  </Box>
                ))}
                {iaLoading && (
                  <Box sx={{ display: 'flex', gap: 0.8, mb: 0.8 }}>
                    <AIIcon sx={{ fontSize: 16, color: '#8b5cf6', mt: 0.3 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CircularProgress size={12} sx={{ color: '#8b5cf6' }} />
                      <Typography sx={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>Pensando...</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 0.5 }}>
                <TextField size="small" fullWidth placeholder="Pregunta al asistente IA..." value={inputIA}
                  onChange={(e) => setInputIA(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !iaLoading) enviarMensajeIA(); }}
                  disabled={iaLoading}
                  sx={{ '& .MuiOutlinedInput-root': { background: '#0f172a', color: '#fff', fontSize: 12, borderRadius: 2 } }}
                />
                <IconButton onClick={enviarMensajeIA} sx={{ color: '#8b5cf6' }}><SendIcon sx={{ fontSize: 18 }} /></IconButton>
              </Box>
            </Box>
          </Box>

          {/* Panel 3: Código vivo */}
          <Box sx={{ width: 260, flexShrink: 0, background: '#0f172a', borderLeft: '1px solid #334155', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <CodeIcon sx={{ fontSize: 16, color: '#10b981' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#10b981', letterSpacing: 0.5, textTransform: 'uppercase' }}>Código vivo</Typography>
              {cambios.length > 0 && <Chip label={cambios.length} size="small" sx={{ height: 18, fontSize: 9, bgcolor: '#10b981', color: '#fff', ml: 'auto' }} />}
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              <pre style={{
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                fontSize: 11, lineHeight: 1.7, color: '#94a3b8', whiteSpace: 'pre-wrap', margin: 0,
              }}>
                {generarPseudocodigo()}
              </pre>
            </Box>
          </Box>
        </Box>
      ) : vistaActiva === 'nueva_func' ? (
        /* Vista Nueva Funcionalidad */
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ maxWidth: 700, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AddIcon sx={{ fontSize: 24, color: '#fff' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Proponer nueva funcionalidad</Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>Describe lo que quieres construir o mejorar en la plataforma</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField size="small" label="Título de la propuesta" placeholder="Ej: Agregar módulo de telemedicina, Nuevo reporte de consultas mensuales..."
                value={nuevaFuncionalidad.titulo} onChange={(e) => setNuevaFuncionalidad(prev => ({ ...prev, titulo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { background: '#1e293b', color: '#fff', fontSize: 13, borderRadius: 2 }, '& .MuiInputLabel-root': { color: '#64748b' } }} />

              <TextField size="small" label="Descripción detallada" multiline minRows={6}
                placeholder={"Describe con el mayor detalle posible:\n\n• ¿Qué problema resuelve?\n• ¿Cómo debería funcionar?\n• ¿Qué pantallas o secciones involucra?\n• ¿Hay alguna referencia o app similar?\n• ¿Quién lo usaría (doctor, paciente, admin)?"}
                value={nuevaFuncionalidad.descripcion} onChange={(e) => setNuevaFuncionalidad(prev => ({ ...prev, descripcion: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { background: '#1e293b', color: '#fff', fontSize: 13, borderRadius: 2, lineHeight: 1.6 }, '& .MuiInputLabel-root': { color: '#64748b' } }} />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel shrink sx={{ color: '#64748b' }}>Prioridad</InputLabel>
                  <Select notched label="Prioridad" value={nuevaFuncionalidad.prioridad} onChange={(e) => setNuevaFuncionalidad(prev => ({ ...prev, prioridad: e.target.value }))}
                    sx={{ background: '#1e293b', color: '#fff', fontSize: 12, borderRadius: 2 }}>
                    <MenuItem value="baja">Baja — Mejora menor</MenuItem>
                    <MenuItem value="normal">Normal — Funcionalidad útil</MenuItem>
                    <MenuItem value="alta">Alta — Necesidad importante</MenuItem>
                    <MenuItem value="urgente">Urgente — Bloquea trabajo</MenuItem>
                  </Select>
                </FormControl>
                <TextField size="small" label="Referencia (opcional)" placeholder="Link a app similar, imagen, o nombre de sistema..."
                  value={nuevaFuncionalidad.referencia} onChange={(e) => setNuevaFuncionalidad(prev => ({ ...prev, referencia: e.target.value }))}
                  InputLabelProps={{ shrink: true }} sx={{ flex: 1, '& .MuiOutlinedInput-root': { background: '#1e293b', color: '#fff', fontSize: 12, borderRadius: 2 }, '& .MuiInputLabel-root': { color: '#64748b' } }} />
              </Box>

              <Button variant="contained" startIcon={<SendIcon />}
                disabled={!nuevaFuncionalidad.titulo || !nuevaFuncionalidad.descripcion}
                onClick={async () => {
                  try {
                    const body = {
                      titulo: nuevaFuncionalidad.titulo,
                      descripcion: nuevaFuncionalidad.descripcion,
                      pantalla: 'nueva_funcionalidad',
                      tipo: 'nueva_funcionalidad',
                      cambios: [{
                        accion: 'nueva_funcionalidad',
                        prioridad: nuevaFuncionalidad.prioridad,
                        referencia: nuevaFuncionalidad.referencia,
                        timestamp: new Date().toISOString(),
                      }],
                      pseudocodigo: `NUEVA FUNCIONALIDAD\n\nTítulo: ${nuevaFuncionalidad.titulo}\nPrioridad: ${nuevaFuncionalidad.prioridad}\n${nuevaFuncionalidad.referencia ? `Referencia: ${nuevaFuncionalidad.referencia}\n` : ''}\n${nuevaFuncionalidad.descripcion}`,
                    };
                    const res = await fetch(`${API_URL}/design-studio/propuestas`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-User-Id': userId }, body: JSON.stringify(body),
                    });
                    if (res.ok) {
                      const data = await res.json();
                      await fetch(`${API_URL}/design-studio/propuestas/${data.id}/enviar-revision`, { method: 'POST', headers: { 'X-User-Id': userId } });
                      setNuevaFuncionalidad({ titulo: '', descripcion: '', prioridad: 'normal', referencia: '' });
                      cargarPropuestas();
                      setVistaActiva('propuestas');
                      agregarMensajeIA(`Propuesta "${body.titulo}" enviada para revisión.`);
                    }
                  } catch (err) { console.error(err); }
                }}
                sx={{
                  borderRadius: 2, fontWeight: 700, fontSize: 14, textTransform: 'none', py: 1.5,
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
                  '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
                  '&:disabled': { background: '#334155', color: '#64748b' },
                }}>
                Enviar propuesta
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        /* Vista de propuestas — timeline con acciones */
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Historial de Propuestas</Typography>
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                {propuestas.filter(p => p.estado === 'en_revision').length} pendientes de revisión · {propuestas.filter(p => p.estado === 'aplicada').length} aplicadas
              </Typography>
            </Box>
            <IconButton onClick={cargarPropuestas} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton>
          </Box>

          {propuestas.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CodeIcon sx={{ fontSize: 48, color: '#334155', mb: 1 }} />
              <Typography sx={{ color: '#64748b', fontSize: 13 }}>No hay propuestas aún</Typography>
              <Typography sx={{ color: '#475569', fontSize: 11, mt: 0.5 }}>Selecciona una pantalla y empieza a diseñar</Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', pl: 3 }}>
              {/* Línea vertical del timeline */}
              <Box sx={{ position: 'absolute', left: 10, top: 0, bottom: 0, width: 2, background: '#334155' }} />

              {propuestas.map((p, idx) => {
                const estadoConfig = {
                  borrador: { color: '#475569', icon: <EditIcon sx={{ fontSize: 14 }} />, label: 'Borrador' },
                  en_revision: { color: '#fbbf24', icon: <PreviewIcon sx={{ fontSize: 14 }} />, label: 'En revisión' },
                  aprobada: { color: '#10b981', icon: <CheckIcon sx={{ fontSize: 14 }} />, label: 'Aprobada' },
                  rechazada: { color: '#ef4444', icon: <CloseIcon sx={{ fontSize: 14 }} />, label: 'Rechazada' },
                  aplicada: { color: '#6366f1', icon: <StarIcon sx={{ fontSize: 14 }} />, label: 'Aplicada' },
                  rollback: { color: '#f59e0b', icon: <UndoIcon sx={{ fontSize: 14 }} />, label: 'Revertida' },
                }[p.estado] || { color: '#475569', icon: <EditIcon sx={{ fontSize: 14 }} />, label: p.estado };

                return (
                  <Box key={p.id} sx={{ position: 'relative', mb: 2 }}>
                    {/* Dot del timeline */}
                    <Box sx={{
                      position: 'absolute', left: -25, top: 12,
                      width: 22, height: 22, borderRadius: '50%',
                      background: '#0f172a', border: `3px solid ${estadoConfig.color}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                    }}>
                      {React.cloneElement(estadoConfig.icon, { sx: { fontSize: 11, color: estadoConfig.color } })}
                    </Box>

                    <Box sx={{ p: 2, borderRadius: 2, background: '#1e293b', border: `1px solid ${p.estado === 'en_revision' ? '#fbbf2440' : '#334155'}`, ml: 1 }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{p.titulo}</Typography>
                            <Chip label={estadoConfig.label} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: estadoConfig.color, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                            {p.pantalla} · {p.cambios?.length || 0} cambios · {p.created_at ? new Date(p.created_at).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Pseudocódigo */}
                      {p.pseudocodigo && (
                        <Box sx={{ p: 1.5, borderRadius: 1.5, background: '#0f172a', border: '1px solid #334155', mb: 1.5 }}>
                          <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, lineHeight: 1.6, color: '#94a3b8', margin: 0, whiteSpace: 'pre-wrap' }}>{p.pseudocodigo}</pre>
                        </Box>
                      )}

                      {/* Comentario de revisión */}
                      {p.comentario_revision && (
                        <Box sx={{ p: 1, borderRadius: 1, background: p.estado === 'rechazada' ? '#7f1d1d20' : '#05966920', border: `1px solid ${p.estado === 'rechazada' ? '#7f1d1d' : '#059669'}40`, mb: 1.5 }}>
                          <Typography sx={{ fontSize: 10, color: p.estado === 'rechazada' ? '#fca5a5' : '#6ee7b7' }}>
                            Revisión: {p.comentario_revision}
                          </Typography>
                        </Box>
                      )}

                      {/* Acciones según estado */}
                      {p.estado === 'en_revision' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                            onClick={async () => {
                              await fetch(`${API_URL}/design-studio/propuestas/${p.id}/aprobar`, { method: 'POST', headers: { 'X-User-Id': userId } });
                              cargarPropuestas();
                            }}
                            sx={{ textTransform: 'none', fontSize: 10, fontWeight: 600, color: '#10b981', border: '1px solid #10b981', borderRadius: 2, '&:hover': { background: '#10b98120' } }}>
                            Aprobar
                          </Button>
                          <Button size="small" startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                            onClick={async () => {
                              const motivo = prompt('Motivo del rechazo:');
                              if (motivo) {
                                await fetch(`${API_URL}/design-studio/propuestas/${p.id}/rechazar`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
                                  body: JSON.stringify({ comentario: motivo }),
                                });
                                cargarPropuestas();
                              }
                            }}
                            sx={{ textTransform: 'none', fontSize: 10, fontWeight: 600, color: '#ef4444', border: '1px solid #ef4444', borderRadius: 2, '&:hover': { background: '#ef444420' } }}>
                            Rechazar
                          </Button>
                        </Box>
                      )}

                      {p.estado === 'borrador' && (
                        <Button size="small" startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                          onClick={async () => {
                            await fetch(`${API_URL}/design-studio/propuestas/${p.id}/enviar-revision`, { method: 'POST', headers: { 'X-User-Id': userId } });
                            cargarPropuestas();
                          }}
                          sx={{ textTransform: 'none', fontSize: 10, fontWeight: 600, color: '#6366f1', border: '1px solid #6366f1', borderRadius: 2, '&:hover': { background: '#6366f120' } }}>
                          Enviar a revisión
                        </Button>
                      )}

                      {p.estado === 'aplicada' && p.rollback_data && (
                        <Button size="small" startIcon={<UndoIcon sx={{ fontSize: 14 }} />}
                          onClick={async () => {
                            if (window.confirm('¿Revertir esta propuesta? Los cambios se deshacerán.')) {
                              await fetch(`${API_URL}/design-studio/propuestas/${p.id}/rollback`, { method: 'POST', headers: { 'X-User-Id': userId } });
                              cargarPropuestas();
                            }
                          }}
                          sx={{ textTransform: 'none', fontSize: 10, fontWeight: 600, color: '#f59e0b', border: '1px solid #f59e0b', borderRadius: 2, '&:hover': { background: '#f59e0b20' } }}>
                          Revertir cambios
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
